import { orderBy, uniqueId } from 'lodash';

import { TRENDING_FLAG, sortAttAsc, sortAttDesc, sortAttName, sortAttPrice, sortAttRank } from '@common/constants';
import { ProductType, SortRefrigeratorBy } from '@common/enums';
import { ApplianceModel, ApplianceSource, ApplianceType } from '@common/models';
import { addMockImage } from '@common/utils';
import { convertProductsToProductDetailsList } from '@common/utils';
import { createSelector } from '@reduxjs/toolkit';

export const convertRefrigeratorsToProductDetails = convertProductsToProductDetailsList(ProductType.REFRIGERATOR);

export const sortRefrigerators = (refrigerators: ApplianceModel[], sortBy?: SortRefrigeratorBy): ApplianceModel[] => {
    switch (sortBy) {
        case SortRefrigeratorBy.HighToLow:
            return orderBy(refrigerators, [sortAttPrice, sortAttRank], [sortAttDesc, sortAttAsc]);
        case SortRefrigeratorBy.LowToHigh:
            return orderBy(refrigerators, [sortAttPrice, sortAttRank], [sortAttAsc, sortAttAsc]);
        case SortRefrigeratorBy.NameAZ:
            return orderBy(refrigerators, [sortAttName], [sortAttAsc]);
        case SortRefrigeratorBy.NameZA:
            return orderBy(refrigerators, [sortAttName], [sortAttDesc]);
        case SortRefrigeratorBy.TrendingNow: {
            const onTrendRefrigerators = orderBy(
                refrigerators.filter((refrigerator: ApplianceModel) =>
                    refrigerator.features.find((item) => item.name.match(TRENDING_FLAG))
                ),
                [sortAttRank],
                [sortAttAsc]
            );

            const otherRefrigerators = orderBy(
                refrigerators.filter(
                    (refrigerator: ApplianceModel) =>
                        !refrigerator.features.find((item) => item.name.match(TRENDING_FLAG)) && refrigerator
                ),
                [sortAttRank],
                [sortAttAsc]
            );

            return [...onTrendRefrigerators, ...otherRefrigerators];
        }
        default:
            return orderBy(refrigerators, [sortAttRank, sortAttName], [sortAttAsc, sortAttAsc]); // may not have a rank, so also try by name
    }
};

// TODO: Consolidate with sinks implemention
type SelectorCallback<T> = (
    data: ApplianceModel[],
    productSource: ApplianceSource,
    applianceType?: ApplianceType,
    shouldAddProductSourceToFeatures?: boolean
) => T;

// TODO: Consolidate with sinks implemention
/**
 * Corrects a given productSourceValue case
 * @param originalProductSource
 * @param correctProductSource
 */
const fixProductSourceCase = (originalProductSource: string, correctProductSource: string) => {
    return originalProductSource
        ? originalProductSource.replace(new RegExp(correctProductSource, 'gi'), correctProductSource)
        : correctProductSource;
};

// TODO: Consolidate with sinks implemention
export const makeRefrigeratorsSelector = () =>
    createSelector<
        [
            SelectorCallback<ApplianceModel[]>,
            SelectorCallback<ApplianceSource>,
            SelectorCallback<ApplianceType | undefined>,
            SelectorCallback<boolean | undefined>
        ],
        ApplianceModel[]
    >(
        [
            (data) => data,
            (_, productSource) => productSource,
            (_, __, applianceType) => applianceType,
            (_, __, ___, shouldAddProductSourceToFeatures) => shouldAddProductSourceToFeatures,
        ],
        (data, productSource, applianceType, shouldAddProductSourceToFeatures = false) => {
            return data
                ?.map((refrigerator) => ({
                    ...refrigerator,
                    uuid: refrigerator.uuid ?? uniqueId(),
                    productSource,
                    applianceType: refrigerator.applianceType ?? applianceType,
                    features: shouldAddProductSourceToFeatures
                        ? [
                              ...(refrigerator.features ?? []),
                              {
                                  name: 'productSource',
                                  description: 'Product Source',
                                  position: 12,
                                  value: refrigerator.productSource // TODO Should we just use the given parameter productSource and ignore what the API provides?
                                      ? fixProductSourceCase(refrigerator.productSource, ApplianceSource.PROGRAM) // fix the case on program products
                                      : productSource,
                              },
                          ]
                        : refrigerator.features ?? [], //TODO: refrigerators.features in data may actually be undefined despite current typing saying otherwise. Fixing types required
                }))
                ?.map(addMockImage);
        }
    );
